import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";

import API_BASE_URL from "../config/Api";
import LogoutButton from "./LogoutButton";
import PasswordField from "./PasswordField";
import ThemeToggle from "./ThemeToggle";
import NotificationCenter from "./NotificationCenter";
import { showToast } from "./Toast";
import { PERMISSIONS, PERMISSION_GROUPS } from "../config/permissions";
import { hasPermission } from "../utils/permissions";

const permissionLabelMap = PERMISSION_GROUPS.reduce((acc, group) => {
    group.permissions.forEach((permission) => {
        acc[permission.code] = permission.label;
    });
    return acc;
}, {});

const getInitials = (email = "") => {
    const value = String(email || "").split("@")[0] || "U";
    return value
        .split(/[._-]/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase())
        .join("") || "U";
};

const getAccessLevel = (permissions = []) => {
    if (permissions.includes("ALL") || permissions.includes("ADMIN")) return "Administrateur";
    if (permissions.some((permission) => permission.includes("_MANAGER"))) return "Manager";
    if (permissions.length > 0) return "Utilisateur autorise";
    return "Profil limite";
};

const getPermissionPreview = (permissions = []) => permissions
    .slice(0, 8)
    .map((permission) => ({ code: permission, label: permissionLabelMap[permission] || permission }));

const getDirectionSummary = (permissions = []) => {
    const groups = PERMISSION_GROUPS
        .filter((group) => group.permissions.some((permission) => permissions.includes(permission.code)))
        .map((group) => group.label);
    return groups.slice(0, 4);
};

const Navbar = ({ onToggleSidebar, sidebarOpen = false }) => {
    const { user, token } = useContext(AuthContext);

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [unreadMail, setUnreadMail] = useState(0);
    const [receptionAlerts, setReceptionAlerts] = useState(0);
    const [erpAlerts, setErpAlerts] = useState(0);
    const [deferredInstallPrompt, setDeferredInstallPrompt] = useState(null);
    const [installHintVisible, setInstallHintVisible] = useState(false);
    const [isStandaloneApp, setIsStandaloneApp] = useState(() => {
        if (typeof window === "undefined") return false;
        return Boolean(window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true);
    });

    const permissions = useMemo(
        () => Array.isArray(user?.permissions) ? user.permissions.map((permission) => String(permission || "").toUpperCase()).filter(Boolean) : [],
        [user?.permissions]
    );
    const permissionPreview = useMemo(() => getPermissionPreview(permissions), [permissions]);
    const directionSummary = useMemo(() => getDirectionSummary(permissions), [permissions]);
    const accessLevel = useMemo(() => getAccessLevel(permissions), [permissions]);
    const userInitials = getInitials(user?.email);
    const sessionStarted = useMemo(() => new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }), []);
    const installHelpText = useMemo(() => {
        if (isStandaloneApp) return "Mode application actif sur cet appareil.";
        if (deferredInstallPrompt) return "Installation disponible directement depuis ce telephone.";

        const hasNavigator = typeof navigator !== "undefined";
        const userAgent = hasNavigator ? navigator.userAgent || "" : "";
        const isIOS = /iphone|ipad|ipod/i.test(userAgent) || (hasNavigator && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
        if (isIOS) return "Sur iPhone, installe depuis Safari avec Partager puis Sur l'ecran d'accueil.";
        return "Sur Android, utilise Chrome puis Installer l'application si le bouton direct n'apparait pas.";
    }, [deferredInstallPrompt, isStandaloneApp]);

    const today = new Intl.DateTimeFormat("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    }).format(new Date());

    const isDisabled = !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword;

    useEffect(() => {
        let cancelled = false;
        let timer = null;

        const loadUnreadMail = async () => {
            if (!user) {
                setUnreadMail(0);
                setReceptionAlerts(0);
                setErpAlerts(0);
                return;
            }
            try {
                const [mailResponse, receptionResponse, erpResponse] = await Promise.allSettled([
                    API_BASE_URL.get("/mail/unread-count"),
                    hasPermission(user, PERMISSIONS.RH_RECEPTION) ? API_BASE_URL.get("/rh_reception/alerts") : Promise.resolve({ data: [] }),
                    hasPermission(user, PERMISSIONS.ERP_NOTIFICATIONS) ? API_BASE_URL.get("/erp/notifications/unread") : Promise.resolve({ data: { value: 0 } }),
                ]);
                if (!cancelled) {
                    if (mailResponse.status === "fulfilled") setUnreadMail(Number(mailResponse.value.data?.count || 0));
                    if (receptionResponse.status === "fulfilled") setReceptionAlerts((receptionResponse.value.data || []).length);
                    if (erpResponse.status === "fulfilled") setErpAlerts(Number(erpResponse.value.data?.value || 0));
                }
            } catch (error) {
                if (!cancelled) {
                    setUnreadMail(0);
                    setReceptionAlerts(0);
                    setErpAlerts(0);
                }
            }
        };

        loadUnreadMail();
        timer = window.setInterval(loadUnreadMail, 60000);

        return () => {
            cancelled = true;
            if (timer) window.clearInterval(timer);
        };
    }, [user]);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;

        const refreshStandaloneMode = () => {
            setIsStandaloneApp(Boolean(window.matchMedia?.("(display-mode: standalone)")?.matches || window.navigator?.standalone === true));
        };

        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setDeferredInstallPrompt(event);
            setInstallHintVisible(false);
        };

        const handleAppInstalled = () => {
            setDeferredInstallPrompt(null);
            setInstallHintVisible(false);
            setIsStandaloneApp(true);
            showToast({
                title: "Application installee",
                message: "SOMA MASTER est maintenant disponible depuis l'ecran d'accueil.",
                type: "success",
            });
        };

        const mediaQuery = window.matchMedia?.("(display-mode: standalone)");

        refreshStandaloneMode();
        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);
        if (mediaQuery?.addEventListener) mediaQuery.addEventListener("change", refreshStandaloneMode);
        else mediaQuery?.addListener?.(refreshStandaloneMode);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
            if (mediaQuery?.removeEventListener) mediaQuery.removeEventListener("change", refreshStandaloneMode);
            else mediaQuery?.removeListener?.(refreshStandaloneMode);
        };
    }, []);

    const resetPasswordForm = () => {
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true);

        if (newPassword !== confirmPassword) {
            showToast({
                title: "Erreur",
                message: "Les mots de passe ne correspondent pas.",
                type: "error",
            });
            setLoading(false);
            return;
        }

        if (newPassword.length < 8) {
            showToast({
                title: "Erreur",
                message: "Le nouveau mot de passe doit contenir au moins 8 caracteres.",
                type: "error",
            });
            setLoading(false);
            return;
        }

        if (new Blob([newPassword]).size > 72) {
            showToast({
                title: "Erreur",
                message: "Le nouveau mot de passe depasse la limite de 72 octets UTF-8.",
                type: "error",
            });
            setLoading(false);
            return;
        }

        try {
            await API_BASE_URL.put(`/users/${user.Id || user.id}/password`, {
                oldPassword: currentPassword,
                newPassword,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            showToast({
                title: "Notification",
                message: "Mot de passe modifie avec succes.",
                type: "success",
            });

            resetPasswordForm();
        } catch (error) {
            showToast({
                title: "Erreur",
                message: error?.response?.data?.message || "Une erreur est survenue lors de la modification.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleInstallApp = async () => {
        if (isStandaloneApp) {
            showToast({
                title: "Mode application actif",
                message: "Tu utilises deja SOMA MASTER comme application mobile.",
                type: "info",
            });
            return;
        }

        if (!deferredInstallPrompt) {
            setInstallHintVisible(true);
            showToast({
                title: "Installation",
                message: "Ouvre le menu du navigateur puis choisis l'ajout a l'ecran d'accueil.",
                type: "info",
            });
            return;
        }

        deferredInstallPrompt.prompt();
        const choice = await deferredInstallPrompt.userChoice;
        setDeferredInstallPrompt(null);

        if (choice?.outcome === "accepted") {
            setInstallHintVisible(false);
            showToast({
                title: "Installation lancee",
                message: "L'icone SOMA MASTER sera ajoutee a l'ecran d'accueil.",
                type: "success",
            });
            return;
        }

        setInstallHintVisible(true);
    };

    return (
        <>
            <nav
                className="layout-navbar container-fluid navbar navbar-expand-xl navbar-detached align-items-center bg-navbar-theme professional-navbar"
                id="layout-navbar"
            >
                <div className="layout-menu-toggle navbar-nav align-items-xl-center me-3 me-xl-0 d-xl-none">
                    <button
                        type="button"
                        className="nav-item nav-link px-0 me-xl-4 navbar-menu-button"
                        aria-label="Ouvrir le menu"
                        aria-expanded={sidebarOpen}
                        aria-controls="layout-menu"
                        onClick={onToggleSidebar}
                    >
                        <i className="bx bx-menu bx-sm"></i>
                    </button>
                </div>

                <div className="navbar-nav-right d-flex align-items-center gap-3 w-100" id="navbar-collapse">
                    <div className="navbar-context">
                        <span>Workspace</span>
                        <strong>SOMA MASTER</strong>
                    </div>

                    <div className="navbar-search">
                        <i className="bx bx-search fs-4 lh-0 me-2"></i>
                        <input
                            type="text"
                            className="form-control border-0 shadow-none"
                            placeholder="Rechercher une entreprise, une facture..."
                            aria-label="Rechercher"
                        />
                        <span className="navbar-search-shortcut">Ctrl K</span>
                    </div>

                    {user && (
                        <ul className="navbar-nav flex-row align-items-center gap-3 ms-auto">
                            <li className="navbar-action-set">
                                <ThemeToggle compact />
                                <NotificationCenter />
                                <Link className="navbar-action-btn navbar-mail-btn" to="/mail" aria-label="Messagerie">
                                    <i className="bx bx-envelope"></i>
                                    {unreadMail > 0 && <span className="navbar-mail-badge">{unreadMail > 99 ? "99+" : unreadMail}</span>}
                                </Link>
                                {hasPermission(user, PERMISSIONS.RH_RECEPTION) && <Link className="navbar-action-btn navbar-mail-btn" to="/rh_reception" aria-label="Rappels reception">
                                    <i className="bx bx-bell"></i>
                                    {receptionAlerts > 0 && <span className="navbar-mail-badge">{receptionAlerts > 99 ? "99+" : receptionAlerts}</span>}
                                </Link>}
                                {hasPermission(user, PERMISSIONS.ERP_NOTIFICATIONS) && <Link className="navbar-action-btn navbar-mail-btn" to="/erp_notifications" aria-label="Notifications ERP">
                                    <i className="bx bx-radar"></i>
                                    {erpAlerts > 0 && <span className="navbar-mail-badge">{erpAlerts > 99 ? "99+" : erpAlerts}</span>}
                                </Link>}
                                {hasPermission(user, PERMISSIONS.FINANCE_FACTURATION) && <Link className="navbar-action-btn" to="/fin_fact_entreprise" aria-label="Facturation finances">
                                    <i className="bx bx-plus"></i>
                                </Link>}
                                {hasPermission(user, PERMISSIONS.FINANCE_CAISSE) && <Link className="navbar-action-btn" to="/fin_cash_exchange_rate" aria-label="Taux de change">
                                    <i className="bx bx-line-chart"></i>
                                </Link>}
                            </li>

                            <li className="navbar-meta">
                                <strong>Espace securise</strong>
                                <span>{today}</span>
                            </li>

                            <li className="nav-item navbar-dropdown dropdown-user dropdown">
                                <button
                                    className="nav-link dropdown-toggle hide-arrow p-0"
                                    data-bs-toggle="dropdown"
                                    style={{ border: "none", background: "transparent" }}
                                    type="button"
                                    aria-label="Menu utilisateur"
                                >
                                    <span className="avatar-shell">
                                        <strong>{userInitials}</strong>
                                    </span>
                                </button>
                                <ul className="dropdown-menu dropdown-menu-end">
                                    <li>
                                        <Link to="/dashboard" className="dropdown-item user-dropdown-head">
                                            <span className="avatar-shell me-3">
                                                <strong>{userInitials}</strong>
                                            </span>
                                            <span className="flex-grow-1">
                                                <span className="fw-semibold d-block">{user.email}</span>
                                                <small className="text-muted">Utilisateur connecte</small>
                                            </span>
                                        </Link>
                                    </li>

                                    <li>
                                        <div className="dropdown-divider"></div>
                                    </li>

                                    <li>
                                        <button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#profileModal">
                                            <i className="bx bx-id-card me-2" aria-hidden="true"></i>
                                            <span className="align-middle">Mon profil</span>
                                        </button>
                                    </li>
                                    <li>
                                        <button className="dropdown-item" data-bs-toggle="modal" data-bs-target="#settingsModal">
                                            <i className="bx bx-lock-alt me-2" aria-hidden="true"></i>
                                            <span className="align-middle">Securite</span>
                                        </button>
                                    </li>

                                    <li>
                                        <div className="dropdown-divider"></div>
                                    </li>

                                    <li>
                                        <button className="dropdown-item text-danger" data-bs-toggle="modal" data-bs-target="#logoutModal">
                                            <i className="bx bx-power-off me-2" aria-hidden="true"></i>
                                            <span className="align-middle">Deconnexion</span>
                                        </button>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    )}
                </div>
            </nav>

            <div className="modal fade" id="profileModal" tabIndex="-1" aria-labelledby="profileModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-xl modal-dialog-centered profile-dialog">
                    <div className="modal-content profile-modal-content">
                        <div className="profile-hero">
                            <div className="profile-identity">
                                <span className="profile-avatar">{userInitials}</span>
                                <div>
                                    <div className="modal-kicker">Mon profil</div>
                                    <h5 className="modal-title" id="profileModalLabel">{user?.email || "Utilisateur"}</h5>
                                    <p>SOMA MASTER - espace personnel et securite du compte</p>
                                </div>
                            </div>
                            <div className="profile-hero-actions">
                                <span className="profile-access-badge"><i className="bx bx-shield-quarter"></i>{accessLevel}</span>
                                <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Fermer"></button>
                            </div>
                        </div>

                        <div className="modal-body profile-body">
                            <section className="profile-stat-grid">
                                <article className="profile-stat-card">
                                    <span><i className="bx bx-key"></i></span>
                                    <div><small>Permissions</small><strong>{permissions.length}</strong><em>Acces attribues</em></div>
                                </article>
                                <article className="profile-stat-card success">
                                    <span><i className="bx bx-layer"></i></span>
                                    <div><small>Directions</small><strong>{directionSummary.length}</strong><em>Groupes actifs</em></div>
                                </article>
                                <article className="profile-stat-card warning">
                                    <span><i className="bx bx-bell"></i></span>
                                    <div><small>Alertes</small><strong>{receptionAlerts + erpAlerts}</strong><em>Reception + ERP</em></div>
                                </article>
                                <article className="profile-stat-card info">
                                    <span><i className="bx bx-time-five"></i></span>
                                    <div><small>Session</small><strong>{sessionStarted}</strong><em>Ouverte aujourd'hui</em></div>
                                </article>
                            </section>

                            <section className="profile-layout-grid">
                                <article className="profile-panel">
                                    <div className="profile-panel-head">
                                        <div><span>Identite</span><strong>Informations du compte</strong></div>
                                        <i className="bx bx-id-card"></i>
                                    </div>
                                    <div className="profile-info-list">
                                        <div><span>Email professionnel</span><strong>{user?.email || "-"}</strong></div>
                                        <div><span>Identifiant systeme</span><strong>#{user?.Id || user?.id || "-"}</strong></div>
                                        <div><span>Niveau d'acces</span><strong>{accessLevel}</strong></div>
                                        <div><span>Mode interface</span><strong>{document.documentElement.getAttribute("data-theme") === "dark" ? "Sombre" : "Clair"}</strong></div>
                                    </div>
                                </article>

                                <article className="profile-panel">
                                    <div className="profile-panel-head">
                                        <div><span>Directions</span><strong>Acces principaux</strong></div>
                                        <i className="bx bx-buildings"></i>
                                    </div>
                                    <div className="profile-direction-list">
                                        {directionSummary.map((direction) => <span key={direction}><i className="bx bx-check-circle"></i>{direction}</span>)}
                                        {directionSummary.length === 0 && <em>Aucune direction attribuee</em>}
                                    </div>
                                </article>

                                <article className="profile-panel profile-permissions-panel">
                                    <div className="profile-panel-head">
                                        <div><span>Autorisations</span><strong>Permissions visibles</strong></div>
                                        <i className="bx bx-lock-open-alt"></i>
                                    </div>
                                    <div className="profile-permission-grid">
                                        {permissionPreview.map((permission) => (
                                            <span key={permission.code}>
                                                <strong>{permission.label}</strong>
                                                <small>{permission.code}</small>
                                            </span>
                                        ))}
                                        {permissions.length > permissionPreview.length && <span className="profile-more-permissions"><strong>+{permissions.length - permissionPreview.length}</strong><small>autres permissions</small></span>}
                                        {permissions.length === 0 && <em>Aucune permission detaillee</em>}
                                    </div>
                                </article>

                                <article className="profile-panel">
                                    <div className="profile-panel-head">
                                        <div><span>Raccourcis</span><strong>Actions rapides</strong></div>
                                        <i className="bx bx-bolt-circle"></i>
                                    </div>
                                    <div className="profile-shortcut-grid">
                                        <Link className="btn btn-primary" to="/dashboard" data-bs-dismiss="modal"><i className="bx bx-grid-alt"></i>Tableau de bord</Link>
                                        <Link className="btn btn-primary" to="/mail" data-bs-dismiss="modal"><i className="bx bx-envelope"></i>Messagerie</Link>
                                        <Link className="btn btn-primary" to="/erp_notifications" data-bs-dismiss="modal"><i className="bx bx-bell"></i>Notifications</Link>
                                        <button className="btn btn-danger" type="button" data-bs-target="#settingsModal" data-bs-toggle="modal"><i className="bx bx-lock-alt"></i>Securite</button>
                                    </div>
                                </article>

                                <article className={`profile-panel profile-install-panel ${isStandaloneApp ? "installed" : ""}`}>
                                    <div className="profile-install-card">
                                        <span className="profile-install-icon"><i className={isStandaloneApp ? "bx bx-check-shield" : "bx bx-mobile-alt"}></i></span>
                                        <div className="profile-install-copy">
                                            <div className="profile-panel-head compact">
                                                <div><span>Mobile</span><strong>Application SOMA</strong></div>
                                            </div>
                                            <p>{installHelpText}</p>
                                            {installHintVisible && (
                                                <div className="profile-install-steps">
                                                    <span><i className="bx bx-info-circle"></i>Si tu vois encore mwaisenicongo.app en haut, tu es dans le navigateur.</span>
                                                    <span>Ouvre l'icone SOMA MASTER ajoutee sur l'ecran d'accueil pour supprimer les barres Safari/Chrome.</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="profile-install-actions">
                                            <button className="btn btn-primary" type="button" onClick={handleInstallApp} disabled={isStandaloneApp}>
                                                <i className={isStandaloneApp ? "bx bx-check" : "bx bx-download"}></i>
                                                {isStandaloneApp ? "Installee" : "Installer"}
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </section>
                        </div>

                        <div className="modal-footer profile-footer">
                            <div>
                                <strong>Compte protege</strong>
                                <span>Garde ton mot de passe confidentiel et ferme la session apres utilisation sur un poste partage.</span>
                            </div>
                            <button className="btn btn-outline-secondary" type="button" data-bs-dismiss="modal">Fermer</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="logoutModal" tabIndex="-1" aria-labelledby="logoutModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-sm modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div>
                                <div className="modal-kicker">Session</div>
                                <h5 className="modal-title" id="logoutModalLabel">Confirmation</h5>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                        </div>
                        <hr className="my-0" />
                        <div className="modal-body text-center">
                            <span className="avatar-shell mx-auto mb-3 modal-avatar">
                                <strong>{userInitials}</strong>
                            </span>
                            <span className="fw-semibold d-block">{user?.email || "Utilisateur"}</span>
                            <small className="text-muted">{user?.email || "Session active"}</small>
                            <p className="mt-3 mb-0">Voulez-vous vraiment vous deconnecter ?</p>
                        </div>
                        <hr className="my-0" />
                        <div className="modal-footer justify-content-center align-items-center">
                            <LogoutButton />
                        </div>
                    </div>
                </div>
            </div>

            <div className="modal fade" id="settingsModal" tabIndex="-1" aria-labelledby="settingsModalLabel" aria-hidden="true">
                <div className="modal-dialog modal-lg modal-dialog-centered security-dialog" role="document">
                    <div className="modal-content security-modal-content">
                        <div className="security-header">
                            <div className="security-title-block">
                                <span><i className="bx bx-lock-alt"></i></span>
                                <div>
                                    <div className="modal-kicker">Parametres</div>
                                    <h5 className="modal-title" id="settingsModalLabel">Securite du compte</h5>
                                    <p>Renouvellement du mot de passe et controle de la session active.</p>
                                </div>
                            </div>
                            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Fermer"></button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body security-body">
                                <div className="security-account-card">
                                    <span className="profile-avatar small">{userInitials}</span>
                                    <div>
                                        <strong>{user?.email || "Utilisateur"}</strong>
                                        <small>{accessLevel} - {permissions.length} permission(s)</small>
                                    </div>
                                    <em>Session active</em>
                                </div>

                                <div className="security-form-grid">
                                    <PasswordField
                                        label="Mot de passe courant"
                                        id="currentPassword"
                                        value={currentPassword}
                                        onChange={(e) => setCurrentPassword(e.target.value)}
                                    />

                                    <PasswordField
                                        label="Nouveau mot de passe"
                                        id="newPassword"
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                    />

                                    <PasswordField
                                        label="Confirmer le nouveau mot de passe"
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </div>

                                <div className="security-hint-card">
                                    <i className="bx bx-info-circle"></i>
                                    <span>Utilise au moins 8 caracteres (72 octets maximum). Evite de partager le mot de passe et change-le apres usage sur un poste public.</span>
                                </div>
                            </div>
                            <div className="modal-footer security-footer justify-content-end">
                                <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal" onClick={resetPasswordForm}>
                                    Annuler
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={isDisabled || loading}>
                                    {loading ? "Modification..." : "Modifier"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Navbar;
